import { useState, useEffect, useRef, useMemo } from "react"
import { useNavigate, useLocation, Link } from "react-router-dom"
import { toast } from 'react-toastify'
import { FaSms } from "react-icons/fa";
import Server from "../../Shared/Server";
import Spinner from '../../Shared/Spinner';
import { FaFileExport } from "react-icons/fa";
import SelectField from "../../Shared/SelectField";
import {years_list, months_list, categories_with_serverside} from "../../Shared/List";

import {AgGridReact} from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

const UnPaid = (props) => {
    const current_user = JSON.parse(localStorage.getItem('user'))
    const billing_date  = current_user?.branch?.billing_date

    const [errors, setErrors] = useState({});
    const [unpaid, setUnPaid] = useState([]);
    const [title, setTitle] = useState('');
    const [pinnedBottomRowData, setPinnedBottomRowData] = useState([]);
    const [yearOptions, setYearOptions] = useState([]);
    const [monthOptions, setMonthOptions] = useState([]);
    const [byOptions, setByOptions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const gridRef = useRef();
    const navigate = useNavigate();
    const location = useLocation();

    const initialBy = location.state?.by
        ? (typeof location.state.by === 'string'
            ? (categories_with_serverside.find(c => c.name.toLowerCase() === location.state.by.toLowerCase())?.id || 1)
            : location.state.by)
        : 1;

    const [formData, setFormData] = useState({
        year: location.state?.year || billing_date.year,
        month: location.state?.month || billing_date.month,
        by: initialBy,
    });
    const {year, month, by} = formData;

    const fetchData = async (payload) => {
        setIsLoading(true)
        try {
            const response = await Server.post('/un-paid/', payload)
            const years = []
            const months = []
            const categories = []

            years_list.map(year => (
                years.push({
                    id: year.id,
                    key: year.key,
                    name: year.name
                })
            ))

            months_list.map(month => (
                months.push({
                    id: month.id,
                    key: month.key,
                    name: month.name
                })
            ))

            categories_with_serverside.map(category => (
                categories.push({
                    id: category.id,
                    key: category.key,
                    name: category.name
                })
            ))

            setUnPaid(response?.data)
            setYearOptions(years)
            setMonthOptions(months)
            setByOptions(categories)
            setIsLoading(false)

            const output = response?.data.reduce((data, row) => {
                data.customers += row.customers;
                data.previous_balance += parseFloat(row.previous_balance);
                data.bill += parseFloat(row.bill);
                data.total += parseFloat(row.total);
                return data;
            }, {customers: 0, previous_balance: 0.00, bill: 0.00, total: 0});
            setPinnedBottomRowData([
                {
                    customers: output.customers,
                    bill: parseFloat(output.bill).toFixed(2),
                    total: parseFloat(output.total).toFixed(2),
                    previous_balance: parseFloat(output.previous_balance).toFixed(2)
                }
            ]);
        } catch (error) {
            setIsLoading(false)
            if(error?.response?.status === 401){
                localStorage.removeItem('user')
                navigate('/login')
            }else if (error?.response?.status === 403){
                toast.error(error?.response?.data?.detail || 'Forbidden');
                navigate('/');
            }else if (error?.response?.status === 402){
                toast.error(error?.response?.data?.detail || 'Payment required');
                navigate('/')
            }else {
                toast.error(error?.response?.data?.detail || 'Failed to load unpaid data')
            }
        }
    }

    const columnDefs = useMemo(() => [
        {
            headerName: "#",
            filter: false,
            maxWidth: 70,
            sortable: true,
            valueFormatter: function (params) {
            if (isNaN(params.node.id)) {
                return "";
            }
            return `${Number(params.node.rowIndex) + 1}`;
            },
        },

        {
            headerName: "Name",
            field: "name",
            sortable: true,
            filter: true,
        },
        {
            headerName: "Customers",
            field: "customers",
            sortable: true,
            filter: 'agNumberColumnFilter',
        },
        {
            headerName: "Prev. Balance",
            field: "previous_balance",
            sortable: true,
            filter: 'agNumberColumnFilter',
        },
        {
            headerName: "Bill",
            field: "bill",
            sortable: true,
            filter: 'agNumberColumnFilter',
        },
        {
            headerName: "Total",
            field: "total",
            sortable: true,
            filter: 'agNumberColumnFilter',
        },
        {
            headerName: "Actions",
            pinned: 'left',
            minWidth: 80,
            sortable: false,
            filter: false,
            cellRenderer: (e) => {
                if (!e.data) return null;
                if (e.node.rowPinned) return '';
                return (
                    <div className="d-flex gap-1">
                        <Link
                            className="btn btn-outline-info"
                            title="Send SMS to unpaid customers"
                            to="/send-unpaid-message"
                            state={{ payload: {
                                employee_id: e.data.employee_id,
                                year: year,
                                month: month,
                                by: categories_with_serverside[formData.by - 1].name.toLowerCase()
                            } }}
                        >
                            <FaSms/>
                        </Link>
                    </div>
                );
            },
        },
    ], []);

    useEffect(() => {
        // get current user to direct collector to his/her list only
        const current_user = JSON.parse(localStorage.getItem('user'));
        const {employee, role} = current_user
        if (role?.name === 'Collector'){
            const payload = {public_id: employee?.public_id, year, month, by: 'collector', name: employee?.full_name}
            navigate(`/un-paid-collector`, { state: {data: payload}})
        }

        const monthName = months_list[formData.month - 1].name
        const categoryName = categories_with_serverside[formData.by - 1].name
        setTitle(`${monthName}, ${year} UnPaid By ${categoryName}`);
        fetchData({year: year, month: month, by: categoryName})
    }, [])

    const onChange = (e) => {
    setFormData((prevState) => ({
        ...prevState,
        [e.target.name]: e.target.value
    }))

    }

    const onDoubleClick = (payload) => {
        navigate(`/un-paid-${payload.by}`, { state: {data: payload}})
    }

    const onSubmit = (e) => {
        e.preventDefault()

        const monthName = months_list[formData.month - 1].name
        const categoryName = categories_with_serverside[formData.by - 1].name
        setTitle(`${monthName}, ${year} UnPaid By ${categoryName}`);
        if (categoryName === 'ServerSide'){
            const payload = {year, month, by: 'serverside', name: 'ServerSide'}
            navigate(`/un-paid-serverside`, { state: {payload: payload}})
        }else{
            const payload = {year, month, by: categoryName}
            fetchData(payload)
        }

    }

    const gridApiRef = useRef(null);

    const onGridReady = (params) => {
        gridApiRef.current = params.api;
    };

    const onExportClick = () => {
        if (gridApiRef.current) {
            gridApiRef.current.exportDataAsCsv({ fileName: `${title}.csv` });
        } else {
            console.error("Grid API is not available yet!");
        }
    };

    const getRowStyle = (params) => {
        if (params.node.rowPinned) {
          return {
            backgroundColor: '#f8f9fa',
            fontWeight: 'bold',
            color: '#000',
          };
        }
        return {};
    };

    if(isLoading){
        return <Spinner/>
    }

  return (
    <>
        <form onSubmit={onSubmit}>
            <div className="row">
                <div className="col-md-2">
                    <SelectField
                        name="year"
                        value={parseInt(year)}
                        options={yearOptions}
                        onChange={onChange}
                        error={errors.year}
                    />
                </div>
                <div className="col-md-2">
                    <SelectField
                        name="month"
                        value={parseInt(month)}
                        options={monthOptions}
                        onChange={onChange}
                        error={errors.month}
                    />
                </div>
                <div className="col-md-2">
                    <SelectField
                        name="by"
                        value={parseInt(by)}
                        options={byOptions}
                        onChange={onChange}
                        error={errors.by}
                    />
                </div>
                <div className="col-md-4">
                    <input type="submit" className="btn btn-info mt-1" value="Apply Filter" style={{float: 'left'}}/>
                </div>
                <div className="col-md-2">
                    <button onClick={()=>onExportClick()} className="btn btn-success mb-2" style={{float: 'right'}}><FaFileExport/> &nbsp;Export</button>
                </div>
            </div>
            <br/>
        </form>
        <div className="card mt-0">
            <div className="card-header p-2 bg-info text-white d-flex justify-content-center">
                <h3 className="card-title">{title}</h3>
            </div>
            <div className="card-body">
                <div className="row">
                    <div className="col-md-12">
                        <div className="ag-theme-alpine responsive-aggrid" style={{ height: 400, width: '100%' }}>
                        <AgGridReact
                            onRowDoubleClicked={(e) => {
                                if (e.node.rowPinned) return '';
                                else if (e.data.customers < 1) return '';
                                return onDoubleClick({
                                    year: parseInt(year),
                                    month: parseInt(month),
                                    public_id: e.data.public_id,
                                    by: categories_with_serverside[formData.by - 1].name.toLowerCase(),
                                    name: e.data.name
                                })
                            }}

                            suppressMenuHide={true}

                            rowData={unpaid}
                            domLayout={"normal"}
                            rowSelection={"multiple"}

                            animateRows={true}

                            pagination={false}
                            onGridReady={onGridReady}

                            resizable={true}
                            enableCellTextSelection={true}
                            suppressCellFocus={true}


                            ref={gridRef}
                            pinnedBottomRowData={pinnedBottomRowData}
                            getRowStyle={getRowStyle}
                            columnDefs={columnDefs}
                        ></AgGridReact>{" "}
                    </div>
                    </div>
                </div>
            </div>
        </div>
      <br/>
    </>
  )
}

export default UnPaid